/**
 * Refund Payment Dialog - Admin only
 *
 * A refund CREDITS the customer's balance and does nothing else. It does not cancel the order and
 * it does not delete a machine that was really built — both are left to the operator afterwards,
 * cancelling an order being its own action with its own confirmation. This dialog therefore says
 * which of those two situations the operator is in BEFORE the money moves, since neither can be
 * undone from here once it has.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminPaymentService } from "@/services/admin-payment.service";
import { formatCurrency } from "@/lib/currency";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { awaitRefresh } from "@/lib/awaitRefresh";

/** The shortest reason the API accepts (RefundPaymentCommandValidator). */
const MIN_REASON_LENGTH = 10;
/**
 * Below the API's 500 on purpose: the reason is written into the ledger row as
 * "Refund for payment: WIC-PAY-…. Reason: <reason>", and that column holds 500 characters, so a
 * longer reason would make the refund fail to save.
 */
const MAX_REASON_LENGTH = 400;

interface RefundPaymentDialogProps {
  paymentId: string;
  paymentAmount: number;
  paymentCurrency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Mirrors the refund mutation's own `isPending` up to the page, so the "Refund Payment" header
   * action can disable itself for the length of the request. Radix's own body `pointer-events:
   * none` already keeps the button unreachable while this dialog is open and modal, but that is
   * an implementation detail of the dialog underneath it — the header should not rely on it.
   */
  onPendingChange?: (pending: boolean) => void;
  /**
   * Whether this dialog covers the whole page while the refund runs. A table that opens it passes
   * `false` and covers its own Card instead, driven by `onPendingChange`.
   */
  pageOverlay?: boolean;
}

export function RefundPaymentDialog({
  paymentId,
  paymentAmount,
  paymentCurrency,
  open,
  onOpenChange,
  onPendingChange,
  pageOverlay = true,
}: RefundPaymentDialogProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  /**
   * A refund request is outstanding.
   *
   * `refundMutation.isPending` only flips on the NEXT render, so two clicks in the same tick
   * both read `false` and both fire. This is the one dialog of the three where that moves
   * money: the second request loses — the payment is already Refunded — and the operator is
   * shown a failure for a refund that went through, which invites them to try again.
   */
  const refundInFlight = useRef(false);

  // Fetched only while the dialog is open: one call per refund the operator is actually
  // considering, not one per row of the payments table.
  const {
    data: preview,
    isLoading: previewLoading,
    isError: previewFailed,
    isFetching: previewFetching,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ["payments", paymentId, "refund-preview"],
    queryFn: () => adminPaymentService.getRefundPreview(paymentId),
    enabled: open,
    staleTime: 0,
  });

  const refundMutation = useMutation({
    mutationFn: () => adminPaymentService.refundPayment(paymentId, { reason }),
    onSuccess: () => {
      toast.success("Payment refunded to the customer's balance");
      // Closing the dialog and clearing the reason are NOT done here: this hook-level
      // `onSuccess` runs BEFORE `onSettled`'s refetch below, so doing it here would drop the
      // dialog while the BusyOverlay it was inside of is still the only thing on screen. See the
      // mutate-level `onSuccess` in `handleRefund`, which runs after that refetch lands.
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to refund payment");
    },
    // Returned: the mutation stays `isPending` — and the BusyOverlay below stays up — until the
    // invalidated list/detail queries have actually refetched, not on the bare API answer.
    // `["payments", paymentId]` is dropped: it is already covered by the `["payments"]` prefix
    // invalidated here.
    //
    // Released on failure too, or one network blip locks the button until the dialog is reopened.
    // `awaitRefresh` caps how long that release (and the overlay) waits on the refetch: the
    // refund itself already landed, so a slow re-read must not hold the button for minutes.
    onSettled: () => {
      refundInFlight.current = false;
      return awaitRefresh(
        queryClient.invalidateQueries({ queryKey: ["payments"] }),
      );
    },
  });

  useEffect(() => {
    onPendingChange?.(refundMutation.isPending);
  }, [refundMutation.isPending, onPendingChange]);

  // Released on unmount as well, matching `PaymentProofPanel`'s `onBusyChange` cleanup: a dialog
  // that goes away mid-refund would otherwise leave the parent's header action disabled forever.
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < MIN_REASON_LENGTH;
  const reasonTooLong = trimmedReason.length > MAX_REASON_LENGTH;

  const handleRefund = () => {
    if (refundInFlight.current) return;

    if (reasonTooShort || reasonTooLong) {
      toast.error(
        `Please give a refund reason between ${MIN_REASON_LENGTH} and ${MAX_REASON_LENGTH} characters`,
      );
      return;
    }
    refundInFlight.current = true;
    refundMutation.mutate(undefined, {
      // Runs after `onSettled`'s refetch lands (see the hook-level `onSuccess` above), so the
      // dialog closes once the refreshed queries are already showing the refund — not while the
      // BusyOverlay is still the only thing on screen.
      onSuccess: () => {
        onOpenChange(false);
        setReason("");
      },
    });
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: trimmedReason.length > 0,
    isSubmitting: refundMutation.isPending,
    onDiscard: () => setReason(""),
  });

  const amount = formatCurrency(paymentAmount, paymentCurrency);
  // The preview's own figure is what actually moves — on a Balance settlement that is the cash
  // the ledger debited for the order, which can be less than `payment.amount` when sign-up bonus
  // credit funded part of it. Fall back to the payment's own amount while the preview is still
  // loading, or unavailable, so the dialog never shows a blank.
  const creditedAmount = preview?.refundAmount ?? paymentAmount;
  const creditedAmountFormatted = formatCurrency(
    creditedAmount,
    paymentCurrency,
  );
  const creditedAmountDiffers =
    preview !== undefined && creditedAmount !== paymentAmount;
  const machines = preview?.order?.provisionedResources ?? [];

  // A preview that could not be loaded must NOT quietly read as "nothing was built" — that is the
  // one reading that would let a live machine be forgotten. Block instead, and say why.
  const blocked = previewFailed || preview?.canRefund === false;

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund Payment</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              {preview?.wouldCreditWallet === false
                ? `${creditedAmountFormatted} is already in the customer's balance from an earlier credit, so this refund moves no money.`
                : `This will credit ${creditedAmountFormatted} back to the customer's balance.`}
              {/* Only a Balance settlement can differ from the payment's own amount, and only
                  because the ledger only ever debited part of it — the rest came from sign-up
                  bonus credit, which is never handed back. A `span`, not a second paragraph:
                  DialogDescription already renders a `<p>`, and nesting one inside it is invalid
                  HTML. */}
              {creditedAmountDiffers && (
                <span className="mt-1 block">
                  {`This payment records ${amount}, but a balance settlement hands back only the cash actually taken for the order.`}
                </span>
              )}
            </DialogDescription>
            {previewLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking what this refund would leave behind…
              </div>
            ) : previewFailed ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p>
                    Could not check what this payment created. Refunding is
                    blocked until it can be read — otherwise a machine that
                    exists could be left running with nobody told about it.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void refetchPreview()}
                    disabled={previewFetching}
                  >
                    {previewFetching && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            ) : preview?.canRefund === false ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{preview.refusalReason}</AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Warning 1 — the machines this refund leaves running. */}
                {machines.length > 0 ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <p>
                        This payment created {machines.length}{" "}
                        {machines.length === 1 ? "machine" : "machines"}, which
                        this refund does <strong>not</strong> delete. Delete
                        {machines.length === 1 ? " it" : " them"} by hand
                        afterwards if the customer should not keep
                        {machines.length === 1 ? " it" : " them"}.
                      </p>
                      <ul className="mt-2 space-y-1">
                        {machines.map((machine) => (
                          <li key={machine.resourceId} className="text-xs">
                            <span className="font-medium">{machine.name}</span>{" "}
                            <span className="opacity-80">
                              ({machine.status})
                            </span>{" "}
                            <span className="font-mono opacity-80">
                              {machine.vmUuid}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      This payment never created a machine, so there is nothing
                      to delete afterwards.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Warning 2 — the order this refund leaves as it is. */}
                {preview?.order ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      Order{" "}
                      <span className="font-medium">
                        {preview.order.orderNumber}
                      </span>{" "}
                      stays as it is ({preview.order.status}). A refund does not
                      cancel it — cancelling is a separate action with its own
                      confirmation.
                    </AlertDescription>
                  </Alert>
                ) : null}
              </>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="reason">Refund Reason *</Label>
                <span
                  className={`text-xs ${
                    reasonTooLong
                      ? "text-destructive-ink"
                      : "text-muted-foreground"
                  }`}
                >
                  {trimmedReason.length}/{MAX_REASON_LENGTH}
                </span>
              </div>
              <Textarea
                id="reason"
                placeholder="Explain why this payment is being refunded (at least 10 characters)…"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                disabled={blocked || refundMutation.isPending}
              />
              {/* The reason reaches the audit row and the customer's own notification, so the API
                  requires it too — saying so here saves a round trip to find that out. */}
              {trimmedReason.length > 0 && reasonTooShort ? (
                <p className="text-xs text-destructive-ink">
                  At least {MIN_REASON_LENGTH} characters.
                </p>
              ) : reasonTooLong ? (
                <p className="text-xs text-destructive-ink">
                  Must not exceed {MAX_REASON_LENGTH} characters.
                </p>
              ) : null}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={refundMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRefund}
              disabled={
                refundMutation.isPending ||
                previewLoading ||
                blocked ||
                reasonTooShort ||
                reasonTooLong
              }
            >
              {refundMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Refunding…
                </>
              ) : (
                "Refund Payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
      <BusyOverlay
        open={refundMutation.isPending && pageOverlay}
        label="Processing refund…"
      />
    </>
  );
}
